<template>
    <div class="modal fade" id="addressRequiredModal" tabindex="-1" aria-labelledby="addressRequiredModalLabel"
         aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title" id="addressRequiredModalLabel">Information Required</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <p>Please select a purchase method and provide your address before adding items to your cart.</p>

                    <div class="dispatch-widget__main mb-3" v-if="bothEnabled || eitherEnabled">
                        <div class="dispatch-widget__options form-group" v-if="bothEnabled">
                            <div class="toggle-switch">
                                <input type="radio" name="modal-dispatch" id="modal-pickup" value="pickup" v-model="dispatchMethod">
                                <label for="modal-pickup">{{ pickupLabel }}</label>

                                <input type="radio" name="modal-dispatch" id="modal-delivery" value="delivery" v-model="dispatchMethod">
                                <label for="modal-delivery">{{ deliveryLabel }}</label>

                                <div class="toggle-slider"></div>
                            </div>
                        </div>
                    </div>

                    <div class="dispatch-widget__output">
                        <div class="dispatch-section" :class="{ 'display-none': (!bothEnabled && !deliveryEnabled) || (bothEnabled && dispatchMethod !== 'delivery') }">
                            <h5 class="dispatch-widget__output__heading">{{ deliveryHeading }}</h5>
                            <DeliveryAddressLookup
                                ref="addressLookup"
                                :google-maps-api-key="googleMapsApiKey"
                                :product-handle="productHandle"
                            ></DeliveryAddressLookup>
                        </div>

                        <div class="dispatch-section" :class="{ 'display-none': (!bothEnabled && !pickupEnabled) || (bothEnabled && dispatchMethod !== 'pickup') }">
                            <h5 class="dispatch-widget__output__heading">{{ pickupHeading }}</h5>
                            <div class="message-box">
                                <div class="message-box__label">
                                    <h4>{{ storeName }}</h4>
                                    <div v-html="formattedAddress"></div>
                                </div>
                                <div class="message-box__value">
                                    <div class="pill pill-primary">Available</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="saveAndContinue" :disabled="!isFormValid">
                        Continue
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import DeliveryAddressLookup from '@/frontend/vue/components/Product/DeliveryAddressLookup.vue';
import {Modal} from 'bootstrap';
import {useDeliveryConfig} from '@/frontend/vue/composables/useDeliveryConfig';

const props = defineProps({
    googleMapsApiKey: {
        type: String,
        default: ''
    },
    businessName: {
        type: String,
        default: 'Store'
    },
    businessAddress: {
        type: String,
        default: ''
    },
    productHandle: {
        type: String,
        default: ''
    },
    onContinue: {
        type: Function,
        default: () => {
        }
    },
    deliveryLabel: {
        type: String,
        default: 'Delivery'
    },
    pickupLabel: {
        type: String,
        default: 'Pickup'
    },
    deliveryHeading: {
        type: String,
        default: 'Enter your address to check delivery availability:'
    },
    pickupHeading: {
        type: String,
        default: 'Nearest Centre with Stock:'
    },
    deliveryEnabled: {
        type: Boolean,
        default: true
    },
    pickupEnabled: {
        type: Boolean,
        default: true
    }
});

const bothEnabled = computed(() => props.deliveryEnabled && props.pickupEnabled);
const eitherEnabled = computed(() => props.deliveryEnabled || props.pickupEnabled);

const defaultDispatchMethod = computed(() => {

    if (typeof window !== 'undefined') {
        const pickupRadio = document.getElementById('pickup');
        const deliveryRadio = document.getElementById('delivery');

        if (pickupRadio && deliveryRadio) {
            if (pickupRadio.checked) {
                return 'pickup';
            }
            if (deliveryRadio.checked) {
                return 'delivery';
            }
        }
    }

    if (props.pickupEnabled && props.deliveryEnabled) {
        try {
            const storedData = sessionStorage.getItem('checkoutAddressData');
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                if (parsedData.dispatch === 'pickup' || parsedData.dispatch === 'delivery') {
                    return parsedData.dispatch;
                }
            }
        } catch (e) {
            console.error('Error reading from sessionStorage:', e);
        }
    }

    if (props.pickupEnabled) {
        return 'pickup';
    }

    if (props.deliveryEnabled) {
        return 'delivery';
    }

    return 'pickup';
});

const { deliveryConfig, getDeliveryConfig } = useDeliveryConfig();

const dispatchMethod = ref(defaultDispatchMethod.value);
const addressLookup = ref(null);
const modal = ref(null);

const storeName = ref(props.businessName);
const storeAddress = ref(props.businessAddress);

const formattedAddress = computed(() => {
    return storeAddress.value.split('\n').join('<br>');
});

const isFormValid = computed(() => {
    if (dispatchMethod.value === 'pickup') {
        return true;
    } else {
        if (!addressLookup.value) return false;

        const details = addressLookup.value.addressDetails;
        const hasAddressDetails = details.address && details.suburb && details.state && details.postcode;

        const deliveryCheckCompleted = addressLookup.value.deliveryStatus !== null;

        return hasAddressDetails && deliveryCheckCompleted;
    }
});

function saveAndContinue() {
    if (dispatchMethod.value === 'pickup') {

        sessionStorage.setItem('checkoutAddressData', JSON.stringify({
            dispatch: 'pickup',
            address: '',
            suburb: '',
            state: '',
            postcode: ''
        }));

        window.dispatchEvent(new CustomEvent('apply-cart'));

        if (modal.value) {
            modal.value.hide();
        }

    } else if (dispatchMethod.value === 'delivery' && addressLookup.value) {

        window.dispatchEvent(new CustomEvent('address-updated'));

        if (modal.value) {
            modal.value.hide();
        }

        if (props.onContinue) {
            props.onContinue();
        }
    } else {
        // This shouldnt be possible but we are capturing it anyway
        if (modal.value) {
            modal.value.hide();
        }
    }
}

onMounted(async () => {

    if (typeof window !== 'undefined') {
        const modalElement = document.getElementById('addressRequiredModal');
        if (modalElement) {
            try {
                // Use the imported Modal class
                modal.value = new Modal(modalElement);
            } catch (error) {
                console.error('Error initializing modal:', error);
            }
        }
    }

    try {
        const config = await getDeliveryConfig();
        if (config) {

            if (config.storeName) {
                storeName.value = config.storeName;
            }

            if (config.storeAddress) {
                storeAddress.value = config.storeAddress;
            }
        }
    } catch (error) {
        console.error('Error fetching delivery config:', error);
    }
});

function show() {

    // Clear address fields when modal is shown
    if (addressLookup.value) {
        addressLookup.value.clearAddress();
    }

    // Re-compute the defaultDispatchMethod value
    dispatchMethod.value = defaultDispatchMethod.value;

    if (modal.value) {
        modal.value.show();
    }
}

defineExpose({
    show
});
</script>

<style scoped>
.modal-dialog {
    max-width: 500px;
}

.modal-dialog .modal-body p {
    font-size: 14px;
}

.modal-footer {
    margin-top: 20px;
}

.modal-footer button {
    margin-right: 10px;
}

.dispatch-section {
    margin-top: 1rem;
}

/* Toggle switch styles */
.toggle-switch {
    position: relative;
    display: flex;
    width: 100%;
    height: 40px;
    background-color: #f5f5f5;
    border-radius: 20px;
    overflow: hidden;
}

.toggle-switch input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-switch label {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0 15px;
    z-index: 1;
    transition: color 0.3s ease;
    color: #666;
}

.toggle-switch input[type="radio"]:checked + label {
    color: #fff;
}

.toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 100%;
    background-color: #333;
    border-radius: 20px;
    transition: transform 0.3s ease;
}

.toggle-switch input[type="radio"]:nth-of-type(1):checked ~ .toggle-slider {
    transform: translateX(0);
}

.toggle-switch input[type="radio"]:nth-of-type(2):checked ~ .toggle-slider {
    transform: translateX(100%);
}

.display-none {
    display: none;
}
</style>

<style>
/* Google Places Autocomplete container needs higher z-index to appear above modal */
.pac-container {
    z-index: 1080 !important;
}

/* Target the new Google Maps PlaceAutocompleteElement dropdown */
gmp-placeautocomplete-dropdown,
.gmp-placeautocomplete-dropdown,
div[class*="gmp-placeautocomplete-dropdown"],
div[class*="gmpx-dropdown"],
div[class*="gmp-dropdown"] {
    z-index: 1080 !important;
}
</style>
